import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getRemoteConfig,
  RemoteConfig,
  fetchAndActivate,
  getString,
  getBoolean,
} from 'firebase/remote-config';
import { firebaseOptions } from './firebase_options';
import {
  RemoteConfigDefaults,
  isBooleanKey,
  isListKey,
  type BooleanKeyType,
  type StringKeyType,
  type ListKeyType,
} from './remote-config-keys';

// Re-export keys for convenience
export { RemoteConfigKeys, ListKeys, BooleanKeys, StringKeys } from './remote-config-keys';

// Initialize Firebase
// Map VITE_ENV values (from .env.* files: development/qa/production) to the
// firebaseOptions keys (dev/qa/prod). Without this, VITE_ENV="production" does
// not match any key, so it silently falls back to the dev Firebase app — which
// makes the prod site read dev-targeted Remote Config values (feature toggles).
const envKeyMap: Record<string, keyof typeof firebaseOptions> = {
  development: 'dev',
  dev: 'dev',
  qa: 'qa',
  production: 'prod',
  prod: 'prod',
};
const rawEnv = (import.meta.env.VITE_ENV || 'production').toLowerCase();
// Default to prod so an unknown/missing env never leaks dev toggles to production.
const env: keyof typeof firebaseOptions = envKeyMap[rawEnv] ?? 'prod';

let app: FirebaseApp;
let remoteConfig: RemoteConfig;
let isInitialized = false;

export const initializeFirebase = () => {
  if (!isInitialized) {
    app = initializeApp(firebaseOptions[env] || firebaseOptions.prod);
    remoteConfig = getRemoteConfig(app);

    // Match employer frontend pattern
    remoteConfig.settings = {
      fetchTimeoutMillis: 10_000,
      minimumFetchIntervalMillis: import.meta.env.DEV ? 0 : 5 * 60 * 1000, // 0 for dev, 5 min for prod
    };

    remoteConfig.defaultConfig = RemoteConfigDefaults;
    isInitialized = true;
  }
  return { app, remoteConfig };
};

// Config readiness tracking
let configReady = false;
const configReadyListeners: Array<() => void> = [];

export const onConfigReady = (listener: () => void) => {
  if (configReady) {
    listener();
  } else {
    configReadyListeners.push(listener);
  }
};

// Fetch and activate remote config
export const fetchRemoteConfig = async (): Promise<boolean> => {
  try {
    const { remoteConfig } = initializeFirebase();
    const activated = await fetchAndActivate(remoteConfig);
    configReady = true;
    configReadyListeners.forEach((cb) => cb());
    configReadyListeners.length = 0;
    return activated;
  } catch {
    configReady = true;
    configReadyListeners.forEach((cb) => cb());
    configReadyListeners.length = 0;
    return false;
  }
};

export const isConfigReady = () => configReady;

// ==================== Typed Getter Functions ====================

export const getRemoteConfigBoolean = (key: BooleanKeyType): boolean => {
  try {
    const { remoteConfig } = initializeFirebase();
    return getBoolean(remoteConfig, key);
  } catch {
    return (RemoteConfigDefaults[key] as boolean) ?? false;
  }
};

export const getRemoteConfigString = (key: StringKeyType | ListKeyType): string => {
  try {
    const { remoteConfig } = initializeFirebase();
    return getString(remoteConfig, key);
  } catch {
    return (RemoteConfigDefaults[key] as string) ?? '';
  }
};

export const getRemoteConfigList = (key: ListKeyType): string[] => {
  try {
    const jsonString = getRemoteConfigString(key);
    if (!jsonString || jsonString === '[]') return [];
    return JSON.parse(jsonString) as string[];
  } catch {
    return [];
  }
};

export const getRemoteConfigValue = (key: string): boolean | string | string[] => {
  try {
    const { remoteConfig } = initializeFirebase();

    if (isBooleanKey(key)) {
      return getBoolean(remoteConfig, key);
    }

    if (isListKey(key)) {
      const jsonString = getString(remoteConfig, key);
      if (!jsonString || jsonString === '[]') return [];
      return JSON.parse(jsonString) as string[];
    }

    return getString(remoteConfig, key);
  } catch {
    return RemoteConfigDefaults[key] ?? '';
  }
};

// ==================== Convenience Functions ====================

export const isFeatureEnabled = (key: BooleanKeyType): boolean => {
  return getRemoteConfigBoolean(key);
};
